import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { ru } from 'date-fns/locale';
import {
  ArrowLeft,
  Check,
  Clock3,
  Inbox,
  Loader2,
  RefreshCw,
  Send,
  UserPlus,
  X,
} from 'lucide-react';
import clsx from 'clsx';
import {
  friendsApi,
  type FriendInfo,
  type FriendRequest,
  type OutgoingFriendRequest,
} from '@/api/friends';
import Avatar from '@/components/ui/Avatar';
import NickName from '@/components/ui/NickName';
import { useMobile } from '@/pages/chat/ChatPage';
import { useSidebarPanelStore } from '@/store/sidebarPanelStore';

type RequestsTab = 'incoming' | 'outgoing';
type RequestAction = 'accept' | 'reject' | 'cancel';

interface RequestActionInput {
  friendshipId: string;
  action: RequestAction;
}

function errorMessage(error: unknown): string {
  const apiMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
  return apiMessage ?? 'Не удалось выполнить действие. Проверьте соединение и повторите попытку.';
}

function RequestPerson({
  user,
  onOpen,
}: {
  user: FriendInfo;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex min-w-0 flex-1 items-center gap-3 rounded-lg text-left outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
      aria-label={`Открыть профиль ${user.displayName || user.username}`}
    >
      <Avatar src={user.avatarUrl} username={user.username} size="lg" />
      <span className="min-w-0 flex-1">
        <NickName user={user} fallback={user.username} className="block text-sm font-semibold text-surface-100" />
        <span className="block truncate text-xs text-surface-500">@{user.username}</span>
      </span>
    </button>
  );
}

function LoadingList() {
  return (
    <div className="space-y-3" aria-label="Загрузка заявок" aria-busy="true">
      {[0, 1, 2].map((item) => (
        <div
          key={item}
          className="h-[92px] animate-pulse rounded-xl border border-surface-800 bg-surface-900"
        />
      ))}
    </div>
  );
}

function EmptyState({ tab }: { tab: RequestsTab }) {
  const incoming = tab === 'incoming';
  const Icon = incoming ? Inbox : Send;

  return (
    <div className="flex min-h-64 flex-col items-center justify-center rounded-2xl border border-dashed border-surface-800 px-6 text-center">
      <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-surface-900 text-surface-500">
        <Icon size={22} aria-hidden="true" />
      </span>
      <h2 className="text-base font-semibold text-surface-200">
        {incoming ? 'Новых заявок нет' : 'Исходящих заявок нет'}
      </h2>
      <p className="mt-1 max-w-sm text-sm leading-6 text-surface-500">
        {incoming
          ? 'Когда кто-то предложит дружбу, заявка появится здесь.'
          : 'Отправленные и ещё не принятые заявки будут показаны здесь.'}
      </p>
    </div>
  );
}

export default function FriendRequestsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { setShowSidebar } = useMobile();
  const setPanel = useSidebarPanelStore((state) => state.setPanel);
  const [pendingActions, setPendingActions] = useState<Record<string, RequestAction>>({});
  const [mutationError, setMutationError] = useState<unknown>(null);

  const tab: RequestsTab = searchParams.get('tab') === 'outgoing' ? 'outgoing' : 'incoming';

  const incomingQuery = useQuery({
    queryKey: ['friends', 'pending'],
    queryFn: () => friendsApi.listPending(),
    staleTime: 10_000,
    refetchOnWindowFocus: true,
  });
  const outgoingQuery = useQuery({
    queryKey: ['friends', 'outgoing'],
    queryFn: () => friendsApi.listOutgoing(),
    staleTime: 10_000,
    refetchOnWindowFocus: true,
  });

  const refreshFriendshipState = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['friends'] }),
      queryClient.invalidateQueries({ queryKey: ['friend-status'] }),
      queryClient.invalidateQueries({ queryKey: ['notifications'] }),
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] }),
    ]);
  };

  const requestActionMutation = useMutation({
    mutationFn: ({ friendshipId, action }: RequestActionInput) => {
      if (action === 'accept') return friendsApi.accept(friendshipId);
      if (action === 'reject') return friendsApi.reject(friendshipId);
      return friendsApi.cancel(friendshipId);
    },
    onSuccess: async (_result, { friendshipId, action }) => {
      if (action === 'cancel') {
        queryClient.setQueryData<OutgoingFriendRequest[]>(['friends', 'outgoing'], (current) =>
          current?.filter((request) => request.id !== friendshipId),
        );
      } else {
        queryClient.setQueryData<FriendRequest[]>(['friends', 'pending'], (current) =>
          current?.filter((request) => request.id !== friendshipId),
        );
      }
      await refreshFriendshipState();
    },
    onError: (error) => setMutationError(error),
    onSettled: (_result, _error, { friendshipId }) => {
      setPendingActions((current) => {
        const next = { ...current };
        delete next[friendshipId];
        return next;
      });
    },
  });

  const runRequestAction = (friendshipId: string, action: RequestAction) => {
    if (pendingActions[friendshipId]) return;
    setMutationError(null);
    setPendingActions((current) => ({ ...current, [friendshipId]: action }));
    requestActionMutation.mutate({ friendshipId, action });
  };
  const incoming = incomingQuery.data ?? [];
  const outgoing = outgoingQuery.data ?? [];
  const activeQuery = tab === 'incoming' ? incomingQuery : outgoingQuery;

  const sortedIncoming = useMemo(
    () => [...incoming].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)),
    [incoming],
  );
  const sortedOutgoing = useMemo(
    () => [...outgoing].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)),
    [outgoing],
  );

  const openProfile = (username: string) => navigate(`/u/${username}`);
  const goBack = () => {
    setPanel('dm');
    setShowSidebar(true);
    navigate('/');
  };

  const setTab = (next: RequestsTab) => {
    setSearchParams(next === 'outgoing' ? { tab: 'outgoing' } : {}, { replace: true });
  };

  return (
    <section className="flex min-h-0 flex-1 flex-col bg-surface-950" aria-labelledby="friend-requests-title">
      <header className="flex min-h-14 flex-shrink-0 items-center gap-3 border-b border-surface-800 px-3 sm:px-5">
        <button
          type="button"
          onClick={goBack}
          className="flex h-11 w-11 items-center justify-center rounded-full text-surface-400 transition-colors hover:bg-surface-800 hover:text-surface-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
          aria-label="Назад к диалогам"
        >
          <ArrowLeft size={20} aria-hidden="true" />
        </button>
        <div className="min-w-0">
          <h1 id="friend-requests-title" className="truncate text-base font-semibold text-surface-100">
            Заявки в друзья
          </h1>
          <p className="text-xs text-surface-500">
            {incoming.length > 0 ? `${incoming.length} входящих ожидают ответа` : 'Управление входящими и исходящими'}
          </p>
        </div>
      </header>

      <div className="border-b border-surface-800 px-3 sm:px-5">
        <div className="mx-auto flex w-full max-w-3xl" role="tablist" aria-label="Тип заявок">
          {([
            ['incoming', 'Входящие', incoming.length, Inbox],
            ['outgoing', 'Исходящие', outgoing.length, Send],
          ] as const).map(([value, label, count, Icon]) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={tab === value}
              onClick={() => setTab(value)}
              className={clsx(
                'flex min-h-12 flex-1 items-center justify-center gap-2 border-b-2 px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-400',
                tab === value
                  ? 'border-brand-400 text-brand-300'
                  : 'border-transparent text-surface-500 hover:text-surface-200',
              )}
            >
              <Icon size={17} aria-hidden="true" />
              <span>{label}</span>
              {count > 0 && (
                <span className="min-w-5 rounded-full bg-brand-600 px-1.5 py-0.5 text-[10px] leading-4 text-white">
                  {count > 99 ? '99+' : count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-4 sm:px-5 sm:py-6">
        <div className="mx-auto w-full max-w-3xl">
          {mutationError !== null && (
            <div
              className="mb-4 rounded-xl border border-red-900/70 bg-red-950/40 px-4 py-3 text-sm text-red-200"
              role="alert"
            >
              {errorMessage(mutationError)}
            </div>
          )}

          {activeQuery.isLoading ? (
            <LoadingList />
          ) : activeQuery.isError ? (
            <div className="flex min-h-64 flex-col items-center justify-center rounded-2xl border border-red-900/60 bg-red-950/20 px-6 text-center">
              <p className="text-sm text-red-200">{errorMessage(activeQuery.error)}</p>
              <button
                type="button"
                onClick={() => activeQuery.refetch()}
                className="mt-4 flex min-h-11 items-center gap-2 rounded-xl bg-surface-800 px-4 text-sm font-medium text-surface-100 transition-colors hover:bg-surface-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
              >
                <RefreshCw size={16} aria-hidden="true" />
                Повторить
              </button>
            </div>
          ) : tab === 'incoming' ? (
            sortedIncoming.length === 0 ? (
              <EmptyState tab="incoming" />
            ) : (
              <div className="space-y-3">
                {sortedIncoming.map((request) => {
                  const pendingAction = pendingActions[request.id];
                  const accepting = pendingAction === 'accept';
                  const rejecting = pendingAction === 'reject';
                  const requestBusy = pendingAction !== undefined;
                  return (
                    <article
                      key={request.id}
                      className="rounded-xl border border-surface-800 bg-surface-900 p-3 shadow-sm sm:p-4"
                    >
                      <div className="flex items-start gap-3">
                        <RequestPerson user={request.sender} onOpen={() => openProfile(request.sender.username)} />
                        <span className="flex flex-shrink-0 items-center gap-1 pt-1 text-[11px] text-surface-600">
                          <Clock3 size={12} aria-hidden="true" />
                          {formatDistanceToNow(new Date(request.createdAt), { addSuffix: true, locale: ru })}
                        </span>
                      </div>
                      {request.message && (
                        <p className="mt-3 rounded-lg bg-surface-950 px-3 py-2 text-sm leading-5 text-surface-300">
                          {request.message}
                        </p>
                      )}
                      <div className="mt-3 grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => runRequestAction(request.id, 'accept')}
                          disabled={requestBusy}
                          className="flex min-h-11 items-center justify-center gap-2 rounded-xl bg-brand-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {accepting ? <Loader2 size={17} className="animate-spin" /> : <Check size={17} />}
                          Принять
                        </button>
                        <button
                          type="button"
                          onClick={() => runRequestAction(request.id, 'reject')}
                          disabled={requestBusy}
                          className="flex min-h-11 items-center justify-center gap-2 rounded-xl border border-surface-700 bg-surface-950 px-3 text-sm font-medium text-surface-300 transition-colors hover:border-red-800 hover:text-red-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {rejecting ? <Loader2 size={17} className="animate-spin" /> : <X size={17} />}
                          Отклонить
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            )
          ) : sortedOutgoing.length === 0 ? (
            <EmptyState tab="outgoing" />
          ) : (
            <div className="space-y-3">
              {sortedOutgoing.map((request) => {
                const canceling = pendingActions[request.id] === 'cancel';
                return (
                  <article
                    key={request.id}
                    className="rounded-xl border border-surface-800 bg-surface-900 p-3 shadow-sm sm:p-4"
                  >
                    <div className="flex items-start gap-3">
                      <RequestPerson user={request.receiver} onOpen={() => openProfile(request.receiver.username)} />
                      <span className="flex flex-shrink-0 items-center gap-1 pt-1 text-[11px] text-surface-600">
                        <Clock3 size={12} aria-hidden="true" />
                        {formatDistanceToNow(new Date(request.createdAt), { addSuffix: true, locale: ru })}
                      </span>
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-3 border-t border-surface-800 pt-3">
                      <span className="flex items-center gap-2 text-xs text-surface-500">
                        <UserPlus size={15} aria-hidden="true" />
                        Ожидает ответа
                      </span>
                      <button
                        type="button"
                        onClick={() => runRequestAction(request.id, 'cancel')}
                        disabled={canceling}
                        className="flex min-h-11 items-center justify-center gap-2 rounded-xl border border-surface-700 px-4 text-sm font-medium text-surface-300 transition-colors hover:border-red-800 hover:text-red-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {canceling ? <Loader2 size={16} className="animate-spin" /> : <X size={16} />}
                        Отменить
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
